import { createContext, useContext, useEffect, useState } from "react";
import { AuthService } from "./api/auth";
import { AuthUser } from "./api/type";
import { setSentryUser } from "./observability/sentry";

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: (action: string) => boolean;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = async () => {
    setIsLoading(true);
    try {
      const userData = await AuthService.getMyProfile();
      setUser(userData);
      setSentryUser(
        userData
          ? { id: userData.id, role: userData.roles?.[0] as string | undefined }
          : null,
      );
    } catch (error) {
      setUser(null);
      setSentryUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const logout = () => {
    setUser(null);
    setSentryUser(null);
    AuthService.logout();
  };

  const hasPermission = (action: string): boolean => {
    if (!user) return false;
    // Simple implementation for now, can be expanded based on role/permissions
    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        hasPermission,
        logout,
        refreshUser: loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}